import React from 'react';
import { ShieldCheck, Box, KeyRound, EyeOff } from 'lucide-react';

const PILLARS = [
  {
    icon: Box,
    title: 'Data minimization by design',
    body:
      'Plugins never receive raw CRM or contact records. They get event types (e.g. opportunity_at_risk, new_meeting_booked), internal or hashed IDs, and minimal typed fields, like deal_size_band = "100k-250k" or stage = "Negotiation". The full object stays inside Forsyt.',
    accent: 'text-blue-700 bg-blue-50 border-blue-200',
  },
  {
    icon: KeyRound,
    title: 'Field-level scopes, OAuth-style',
    body:
      'Each plugin declares the kinds of data it needs (e.g. "deal size band, stage, industry bucket"). At install, the customer reviews and approves the scope set, the same way you approve scopes for a mobile app. Nothing outside the approved scope is ever passed.',
    accent: 'text-amber-700 bg-amber-50 border-amber-200',
  },
  {
    icon: ShieldCheck,
    title: 'Execution isolation per tenant',
    body:
      'Every plugin runs in a sandbox per tenant per execution, container, serverless function, or WebAssembly. No outbound network access to your CRM or internal databases. The sandbox can only talk to the Forsyt orchestrator over a narrow, audited RPC interface.',
    accent: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  },
  {
    icon: EyeOff,
    title: 'Partners never see your data',
    body:
      'Plugin authors ship code or a declarative spec. Only Forsyt infrastructure runs that code against your data. Authors get aggregated metrics and test fixtures we expose, never live customer data, never your contacts, never your conversations.',
    accent: 'text-violet-700 bg-violet-50 border-violet-200',
  },
];

const PluginSecurityPosture = () => (
  <section className="py-20 bg-slate-50 border-t border-slate-100" data-testid="plugin-security-posture">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="max-w-3xl mb-12">
        <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-700 bg-white border border-slate-200 px-3 py-1 rounded-full mb-5">
          Security &amp; isolation
        </div>
        <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight leading-[1.05]">
          The plugin model your security team will sign off on.
        </h2>
        <p className="mt-5 text-lg text-slate-600 leading-relaxed">
          A revenue-team plugin store only works if the data does not leak. Forsyt's plugin runtime is
          built so authors can ship powerful playbooks without ever touching your raw CRM, calls, or
          contact data. Four guarantees the entire store is built on.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {PILLARS.map((p) => {
          const Icon = p.icon;
          return (
            <div
              key={p.title}
              data-testid={`security-pillar-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
              className="bg-white rounded-2xl border border-slate-200 p-7 hover:border-slate-300 hover:shadow-md transition-all"
            >
              <div className={`inline-flex items-center justify-center w-11 h-11 rounded-xl border ${p.accent} mb-4`}>
                <Icon className="w-5 h-5" strokeWidth={2.2} />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">{p.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{p.body}</p>
            </div>
          );
        })}
      </div>

      <div className="mt-12 p-6 bg-white border border-slate-200 rounded-2xl text-sm text-slate-600 leading-relaxed">
        <strong className="text-slate-900">Where to look:</strong> open any plugin's detail page and
        scroll the right rail. Every plugin lists the exact scope tokens it requests, in the same format
        the customer approves at install. No hidden access, no quiet escalation.
      </div>
    </div>
  </section>
);

export default PluginSecurityPosture;
